import clsx from 'clsx'
import Card from 'components/Card'
import React from 'react'
import {
    ComponentSubjectContext,
    useComponentSubjectSlot,
} from '../verification/componentSubjectSlot'
import { VerificationBadge } from '../verification/VerificationBadge'

// Shared primitives for the Design System showcase. Kept in one module so each
// per-component showcase file can compose them without re-implementing the
// section chrome, code blocks or copy affordances.

export function copy(value: string): void {
    if (navigator.clipboard) {
        navigator.clipboard.writeText(value).catch(() => {
            /* clipboard is best-effort */
        })
    }
}

export const MIcon: React.FC<{ name: string; className?: string }> = ({ name, className }) => (
    <i className={clsx('material-symbols-outlined leading-[1]', className)} aria-hidden="true">
        {name}
    </i>
)

export interface CopyableProps {
    label: string
    value: string
    className?: string
    // Renders a trailing copy glyph so devs can see the value is copyable. Used
    // for the Tailwind class lines in the Foundations tokens.
    icon?: boolean
}

export const Copyable: React.FC<CopyableProps> = ({
    label,
    value,
    className = '',
    icon = false,
}) => (
    <button
        type="button"
        onClick={() => copy(value)}
        title={`Copy "${value}"`}
        className={clsx(
            'group inline-flex items-center gap-spacing-xs text-left font-mono text-xs text-text-secondary hover:text-text-primary',
            className,
        )}>
        <span className="group-hover:underline">{label}</span>
        {icon && (
            <MIcon
                name="content_copy"
                className="text-sm text-text-quaternary transition-colors group-hover:text-text-tertiary"
            />
        )}
    </button>
)

interface SectionProps {
    title: string
    description?: React.ReactNode
    children: React.ReactNode
}

export const Section: React.FC<SectionProps> = ({ title, description, children }) => {
    // A component showcase renders inside a subject slot, so its outermost
    // section carries the verification badge in front of the card title.
    // Foundations sections have no subject here; their tokens are badged
    // individually on the page itself.
    const subject = useComponentSubjectSlot()

    const card = (
        <Card
            useToggle
            header={
                subject ? (
                    <span className="inline-flex items-center gap-spacing-md">
                        <VerificationBadge subject={subject} />
                        {title}
                    </span>
                ) : (
                    title
                )
            }>
            {description && (
                <div className="mb-spacing-xl text-sm text-text-secondary">{description}</div>
            )}
            {children}
        </Card>
    )

    if (!subject) {
        return card
    }

    return <ComponentSubjectContext.Provider value={null}>{card}</ComponentSubjectContext.Provider>
}

export const InlineCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <code className="rounded-radius-xs bg-background-bg-tertiary px-spacing-xs py-spacing-xxs font-mono text-xs text-text-primary">
        {children}
    </code>
)

export const CodeSnippet: React.FC<{ code: string }> = ({ code }) => (
    <div className="relative">
        <pre className="overflow-x-auto rounded-radius-xs bg-neutral-900 p-spacing-lg pr-spacing-7xl font-mono text-xs text-base-white">
            {code}
        </pre>
        <button
            type="button"
            onClick={() => copy(code)}
            title="Copy snippet"
            className="absolute right-2 top-2 rounded-radius-xs bg-neutral-700 px-spacing-md py-spacing-xs text-xs text-base-white hover:bg-neutral-600">
            Copy
        </button>
    </div>
)

export const titleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

// Fills the 16px avatar slot so the Avatar adornment shows real content.
export const DemoAvatar: React.FC = () => (
    <span className="flex h-full w-full items-center justify-center bg-brand-500 text-[8px] font-semibold text-base-white">
        OR
    </span>
)
